/**
 * Platform stats — shared type + helpers used by the public landing page
 * to display headline metrics (writers, journals, subscriptions, paid out).
 *
 * `roundStat` is a presentation rounder: small numbers display exactly,
 * larger numbers are rounded down and flagged with a "+" to suggest
 * at-least semantics without implying spurious precision.
 */

export interface PlatformStats {
  writers: number;
  journals: number;
  subscriptions: number;
  paidOut: number;
}

export interface RoundedStat {
  value: number;
  suffix: string;
}

/**
 * Round a raw number into a display-friendly value + suffix.
 * - `< 100`: exact value, no suffix.
 * - `< 1,000`: rounded down to nearest 10, "+" suffix.
 * - `>= 1,000`: rounded down to nearest 100, "+" suffix.
 */
export function roundStat(n: number): RoundedStat {
  if (n < 100) return { value: n, suffix: '' };
  if (n < 1000) return { value: Math.floor(n / 10) * 10, suffix: '+' };
  return { value: Math.floor(n / 100) * 100, suffix: '+' };
}

/** Sensible fallback values when the /stats endpoint is unreachable. */
export const FALLBACK_STATS: PlatformStats = {
  writers: 2400,
  journals: 48000,
  subscriptions: 12000,
  paidOut: 310000,
};
